import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { HeaderComponent } from "@components/header/header.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ButtonComponent } from "~app/common/components/button/button.component";
import { ModalComponentMediator } from "~app/common/components/modal/modal-component-mediator";
import { KundorderTyp } from "~app/common/models/constants";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { CreateKundorderCommand } from "../commands/CreateKundorderCommand";
import { KundorderradCreateComponent } from "../kundorder-details/kundorderrad-create/kundorderrad-create.component";
import { KundorderService } from "../kundorder.service";
import { KundorderDetailRequestDTO } from "../models/KundorderDetailRequestDTO";
import { IKundorderDetailGrunddata, IKundorderDetailKundorder, IKundorderDetailKundorderrad, IMattslag } from "../models/kundorder-detail-model";
import { KundorderValidation } from "./kundorder.validation";

@Component({
    selector: "rask-create-kundorder-modal",
    templateUrl: "./create-kundorder-modal.component.html",
    styleUrls: ["./create-kundorder-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        HeaderComponent,
        KundorderradCreateComponent,
        SaveButtonComponent,
        TranslatePipe,
        ButtonComponent
    ]
})
export class CreateKundorderModalComponent implements OnInit {
    command: CreateKundorderCommand;
    form: UntypedFormGroup;
    grunddata: IKundorderDetailGrunddata;
    kundorder: IKundorderDetailKundorder;
    kundorderTyp: string;
    kundorderrad: IKundorderDetailKundorderrad;
    validaitonContext: IValidationContext;
    validation: IValidationScope;

    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly formBinder = inject(FormBinderService);
    private readonly kundorderService = inject(KundorderService);
    private readonly modalComponentMediator = inject(ModalComponentMediator);
    private readonly translationService = inject(TranslateService);

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("data") set data(value) {
        this.kundorderTyp = value;
    }

    attachKundorderrad() {
        this.command.mottagningsplatsId = this.kundorderrad.mottagningsplats.id;
        this.command.lagerplatser = this.kundorderrad.lagerplatser?.map(l => l.id);
        this.command.handelssortimentId = this.kundorderrad.handelssortiment.id;
        this.command.kvantitet = this.kundorderrad.kvantitet;
        this.command.periodFrom = this.kundorderrad.periodFrom.toString();
        this.command.periodTom = this.kundorderrad.periodTom.toString();
        this.command.manuellPaArbetsorder = this.kundorderrad.manuellPaArbetsorder.valueOf();
        this.command.egenskapskodX = this.kundorderrad.egenskapskodX.valueOf();
        this.command.ejVirkeskop = this.kundorderrad.ejVirkeskop.valueOf();
    }

    bindControlToCommand() {
        const binder = new CommandControlBinder(this.kundorderTyp, this.commandService, CreateKundorderCommand);
        const ctrls = this.form.controls;
        binder.bindControl(ctrls.kund, (v, c) => c.kundId = v?.id);
        binder.bindControl(ctrls.mattslag, "mattslag");
        binder.onUpdate.subscribe(c => this.command = c);
    }

    canSave() {
        return !this.form.invalid;
    }

    close() {
        this.modalComponentMediator.getRef().close(null);
    }

    initValidation(): void {
        this.validaitonContext = KundorderValidation.init(this.translationService, this.kundorder as any as KundorderDetailRequestDTO);
        this.validation = this.validaitonContext.current;
        this.formBinder.attachValidation(this.validation, this.form);
    }

    isKundorder = () => this.kundorderTyp === KundorderTyp.Kundorder;

    kundViewFormatter = (v: ITypeAheadResult) => v.namn;

    // Måttslag
    mattslagFormatter = (m: IMattslag) => m?.kortnamn;

    ngOnInit(): void {
        this.kundorder = new KundorderDetailRequestDTO() as any;
        this.form = this.formBinder.CreateGroup(this.kundorder);
        this.busyService.addWork(this.kundorderService.getGrunddata()).subscribe(r => this.grunddata = r);
        this.initValidation();
        this.bindControlToCommand();
    }

    onModelCreated(model) {
        this.kundorderrad = model;
    }

    save() {
        this.attachKundorderrad();
        this.busyService.addWork(this.commandService.pushCommandQueueTransaction()).subscribe(() => {
            this.close();
        });
    }

    // Kund
    searchKund = (str: string) => this.kundorderService.searchForKundTypeAhead(str);
}
